<template>
  <base-content>
    <template v-slot:top>
      <hero-header heading="Your Bank Accounts">
        <base-button modal="#addBankAccountModal" secondary>
          Add Bank Account
        </base-button>
      </hero-header>
    </template>

    <base-card :loading="pending" tabled>
      <template v-if="bankAccounts.length">
        <div class="d-none d-sm-block">
          <base-table>
            <thead>
              <tr>
                <th>Bank Name</th>
                <th>Account Number</th>
                <th>Sort Code</th>
                <th>IBAN</th>
                <th>Status</th>
                <th>Verification</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(account, key) in bankAccounts" :key="key">
                <td>{{ account.bank }}</td>
                <td>{{ account.accountNumber }}</td>
                <td>{{ account.sortCode }}</td>
                <td>{{ account.iban }}</td>
                <td class="font-weight-semibold">
                  <template v-if="account.status === 'APPROVED'">
                    <span class="text-success">{{ account.status }}</span>
                  </template>
                  <template v-else>
                    {{ account.status }}
                  </template>
                </td>
                <td class="text-center">
                  <div v-if="account.kyc" class="font-weight-semibold">
                    SUBMITTED
                  </div>
                  <div v-else-if="account.status !== 'APPROVED'">
                    <base-button small @click="startKyc(key)">
                      Upload KYC
                    </base-button>
                  </div>
                </td>
                <td class="text-right">
                  <div class="btn-group">
                    <base-button icon-right="trash" :loading="removing.includes(account._id)" small @click="remove(key)">
                      Delete
                    </base-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </base-table>
        </div>
        <div class="d-sm-none p-4">
          <div v-for="(account, key) in bankAccounts" :key="key" class="mb-4">
            <p class="mb-0">
              <strong>Bank Name: </strong>{{ account.bank }}<br>
              <strong>Account Number: </strong>{{ account.accountNumber }}<br>
              <strong>Sort Code: </strong>{{ account.sortCode }}<br>
              <strong>IBAN: </strong>{{ account.iban }}<br>
            </p>
            <p class="mb-0">
              <strong>STATUS: </strong><span class="font-weight-semibold" :class="{ 'text-success': account.status === 'APPROVED' }">{{ account.status }}</span>
            </p>
            <div class="mb-3">
              <strong>Verification: </strong>
              <div v-if="account.kyc" class="d-inline-block font-weight-semibold">
                SUBMITTED
              </div>
              <div v-else-if="account.status !== 'APPROVED'" class="d-inline-block">
                <base-button small @click="startKyc(key)">
                  Upload KYC
                </base-button>
              </div>
              <div v-else class="d-inline-block">
                N/A
              </div>
            </div>
            <p class="mb-0">
              <base-button icon-right="trash" :loading="removing.includes(account._id)" small @click="remove(key)">
                Delete Bank Account
              </base-button>
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <p>
          You haven't added any bank accounts yet, <a href="#" @click.prevent="$modal.show('#addBankAccountModal')">add a bank account name</a>.
        </p>
      </template>
      <bank-account-create @saved="refresh" />
      <kyc-document-submit type="2" @submitted="uploaded">
        Please upload a bank statement that contains your name and address for the bank account you're trying to verify.
      </kyc-document-submit>
    </base-card>
  </base-content>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import BankAccountCreate from '@/components/BankAccountCreate.vue'
import HeroHeader from '@/components/HeroHeader.vue'
import KycDocumentSubmit from '@/components/KycDocumentSubmit'

export default {
  components: {
    BankAccountCreate,
    HeroHeader,
    KycDocumentSubmit
  },

  metaInfo: {
    title: 'Bank Accounts'
  },

  data: () => ({
    kyc: null,
    loaded: false,
    removing: [],
    saving: false
  }),

  async created () {
    await this.refresh()

    this.loaded = true
  },

  computed: {
    ...mapState({
      bankAccounts: store => store.core.bankAccounts.entities,
      pending: store => store.core.bankAccounts.isAllPending
    })
  },

  methods: {
    async refresh () {
      await this.getBankAccounts()
    },

    async remove (index) {
      await this.$dialog.confirm('Are you sure you want to delete this bank account?', async () => {
        const account = this.bankAccounts[index]

        this.removing.push(account._id)

        await this.$store.dispatch('core/bank-accounts/remove', account)

        this.$flash.success('Bank account successfully deleted.')
      })
    },

    startKyc (index) {
      this.kyc = this.bankAccounts[index]

      this.$modal.show('#kycDocumentSubmit')
    },

    async uploaded (kyc) {
      await this.updateBankAccount({
        _id: this.kyc._id,
        kyc: kyc._id
      })

      this.refresh()

      this.$modal.hide('#kycDocumentSubmit')
    },

    ...mapActions({
      getBankAccounts: 'core/bank-accounts/all',
      updateBankAccount: 'core/bank-accounts/update'
    })
  }
}
</script>
